import { useResizeObserver } from '@mantine/hooks';
import type { Feature, FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import { geoPath, geoConicConformal } from 'd3-geo';
import { ReactNode, useMemo } from 'react';

import {
  ChoroplethColorStops,
  getChoroplethFillColor,
} from './choropleth-colors';

import classes from './france-departments-choropleth.module.css';

export type ChoroplethMetricMode =
  | 'hectares'
  | 'carbon_points'
  | 'analysis_count'
  | 'granulo_points'
  | 'granulo_analysis_count'
  | 'total_sampling_points'
  | 'total_analysis_count';

export type DepartmentFeatureProperties = {
  code: string;
  name: string;
  total_area: number;
  map_value?: number;
  actual_area?: number;
  projected_area?: number;
  projected_volume_t?: number;
  combined_area?: number;
  parcelle_count: number;
  ilot_count: number;
  carbon_points: number;
  analysis_count: number;
  granulo_points?: number;
  granulo_analysis_count?: number;
  total_sampling_points?: number;
  total_analysis_count?: number;
  nom?: string;
  fill_color?: string;
};

type DepartmentFeature = Feature<Polygon | MultiPolygon, DepartmentFeatureProperties>;

export type FranceDepartmentsChoroplethProps = {
  data: FeatureCollection<Polygon | MultiPolygon, DepartmentFeatureProperties>;
  colorStops: ChoroplethColorStops;
  fillColorMode?: 'gradient' | 'feature';
  /** Per-department fill (solid color or `url(#pattern-id)`). Overrides feature fill_color. */
  departmentFills?: ReadonlyMap<string, string>;
  metricMode?: ChoroplethMetricMode;
  svgDefs?: ReactNode;
  hoveredCode?: string | null;
  selectedCode?: string | null;
  onHover?: (code: string | null) => void;
  onSelect?: (properties: DepartmentFeatureProperties | null) => void;
};

function fitProjection(
  features: DepartmentFeature[],
  width: number,
  height: number,
) {
  const projection = geoConicConformal()
    .center([2.454071, 46.279229])
    .scale(1)
    .translate([0, 0]);

  const path = geoPath(projection);
  const collection: FeatureCollection = {
    type: 'FeatureCollection',
    features,
  };
  const bounds = path.bounds(collection);
  const dx = bounds[1][0] - bounds[0][0];
  const dy = bounds[1][1] - bounds[0][1];

  if (dx === 0 || dy === 0) {
    return { path, projection };
  }

  const scale = 0.92 / Math.max(dx / width, dy / height);
  const translate: [number, number] = [
    (width - scale * (bounds[0][0] + bounds[1][0])) / 2,
    (height - scale * (bounds[0][1] + bounds[1][1])) / 2,
  ];

  projection.scale(scale).translate(translate);

  return { path: geoPath(projection), projection };
}

function getDepartmentLabelValue(
  properties: DepartmentFeatureProperties,
  metricMode: ChoroplethMetricMode,
): number | null {
  if (metricMode === 'hectares') {
    return null;
  }
  const value = properties.map_value ?? 0;
  return value > 0 ? value : null;
}

export function FranceDepartmentsChoropleth({
  data,
  colorStops,
  fillColorMode = 'gradient',
  departmentFills,
  metricMode = 'hectares',
  hoveredCode = null,
  selectedCode = null,
  onHover,
  onSelect,
  svgDefs,
}: FranceDepartmentsChoroplethProps) {
  const [containerRef, rect] = useResizeObserver();

  const { width, height } = rect;
  const features = data.features as DepartmentFeature[];
  const showLabels = metricMode !== 'hectares';

  const pathGenerator = useMemo(() => {
    if (!width || !height || features.length === 0) {
      return null;
    }
    return fitProjection(features, width, height).path;
  }, [features, height, width]);

  return (
    <div className={classes.container} ref={containerRef}>
      {pathGenerator && width > 0 && height > 0 ? (
        <svg
          aria-label="France departments choropleth"
          className={classes.svg}
          height={height}
          role="img"
          viewBox={`0 0 ${width} ${height}`}
          width={width}
        >
          {svgDefs}
          {features.map((feature, index) => {
            const properties = feature.properties;
            if (!properties?.code) {
              return null;
            }

            const code = properties.code;
            const isHovered = hoveredCode === code;
            const isSelected = selectedCode === code;
            const path = pathGenerator(feature);
            const mapValue = properties.map_value ?? properties.total_area ?? 0;
            const labelValue = getDepartmentLabelValue(properties, metricMode);
            const centroid = labelValue !== null ? pathGenerator.centroid(feature) : null;
            const departmentFill = departmentFills?.get(code);
            const fill =
              departmentFill ??
              (fillColorMode === 'feature' && properties.fill_color
                ? properties.fill_color
                : getChoroplethFillColor(mapValue, colorStops));

            if (!path) {
              return null;
            }

            return (
              <g key={`${code}-${index}`}>
                <path
                  className={classes.department}
                  d={path}
                  fill={fill}
                  onClick={() => onSelect?.(properties)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onSelect?.(properties);
                    }
                  }}
                  onMouseEnter={() => onHover?.(code)}
                  onMouseLeave={() => onHover?.(null)}
                  stroke={
                    isSelected ? '#212529' : isHovered ? '#495057' : '#ced4da'
                  }
                  strokeWidth={isSelected ? 2 : isHovered ? 1.5 : 0.6}
                  tabIndex={0}
                />
                {showLabels && labelValue !== null && centroid ? (
                  <text
                    className={classes.label}
                    dominantBaseline="middle"
                    pointerEvents="none"
                    textAnchor="middle"
                    x={centroid[0]}
                    y={centroid[1]}
                  >
                    {labelValue}
                  </text>
                ) : null}
              </g>
            );
          })}
        </svg>
      ) : null}
    </div>
  );
}

export default FranceDepartmentsChoropleth;
